import { Icon } from '@iconify/react'
import React, { useEffect, useState } from 'react'
import {
  withStyles,
  Typography,
  Popover,
  Select,
  MenuItem,
  FormControl,
  Divider,
} from '@material-ui/core'
import styles from './styles'
import {
  CoreToggleButtonGroup,
  CoreToggleButton,
  CoreSlider,
  CoreBackgroundPosition,
} from 'src/components/core'
import { ChromePicker } from 'react-color'
import { track } from '@amplitude/analytics-browser'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import ContainerMediaGalery from 'src/container/image-gallery'
import { useDispatch } from 'react-redux'
import CustomizedSwitch from 'src/components/customized-switch'
import {
  SizeType,
  handleAlignment,
  handleBackground,
  handleSize,
  handleImageDelete,
  handleOverlayColor,
  handlePosition,
  handleColor,
  handleImageOverlay,
  handleButtonWidth,
  handleContainerWidth,
} from '../utils'

const BlockOptionsModal = props => {
  const { classes, editor, selection } = props
  const [background, setBackground] = useState('')
  const [sizing, setSizing] = useState('')
  const [buttonWidth, setButtonWidth] = useState('')
  const [imageURL, setImageURL] = useState('')
  const [size, setSize] = useState('SCALE_TO_FILL')
  const [alignment, setAlignment] = useState('center')
  const [textColor, setTextColor] = useState()
  const [opacityValue, setOpacityValue] = useState('0')
  const [overlayColor, setOverlayColor] = useState('#fff')
  const [pickerVisibles, setPickerVisibles] = useState({
    fontColor: false,
    backgroundColor: false,
    cardBackgroundColor: false,
    buttonColor: false,
  })
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = useState(null)
  const [pageReady, setPageReady] = useState(true)
  const [imagePosition, setImagePosition] = useState('')
  const [isChecked, setIsChecked] = useState(true)

  const onHandleChange = () => {
    setIsChecked(!isChecked)
    editor.editing.view.focus()
    handleImageOverlay(editor, selection, isChecked)
  }

  const onHandleAlignment = (event, newAlignment) => {
    if (newAlignment !== null) {
      setAlignment(newAlignment)
      editor.editing.view.focus()
      handleAlignment(editor, selection, newAlignment)
    }
  }

  const onHandleBackground = (event, newBackground) => {
    if (newBackground === 'image') {
      setTextColor('#ffffff')
    } else {
      setImageURL('')
      setBackground('')
      setImagePosition('center')
      setSize('SCALE_TO_FILL')
    }
    setBackground(newBackground)
    if (newBackground !== null) {
      editor.editing.view.focus()
      handleBackground(editor, selection, newBackground)
    }
  }

  useEffect(() => {
    const selectedElement = selection
    const child = selection.getChild(0).getChild(0).getChild(0)
    if (child.getClassNames('button-wrapper')) {
      setButtonWidth(child.getAttribute('data-sizing'))
      setAlignment(child.getAttribute('data-alignment') || 'center')
    }
    setTextColor(selectedElement.getAttribute('background-color') || '#ffffff')
    setBackground(selectedElement.getAttribute('background-type') || 'color')
    setImageURL(selectedElement.getAttribute('background-image'))
    setImagePosition(
      selectedElement.getAttribute('background-position') || 'center',
    )
    setSize(selectedElement.getAttribute('size-type') || 'SCALE_TO_FILL')
    setIsChecked(selectedElement.getAttribute('use-image-overlay') === 'true')
    setSizing('fit')
  }, [selection])

  const handleSliderChange = value => {
    setOpacityValue(value)
    handleOverlayColor(editor, selection, overlayColor, value, imageURL)
  }

  const onHandleContainerWidth = (event, newSizing) => {
    if (newSizing !== null) {
      setSizing(newSizing)
      handleContainerWidth(editor, selection, newSizing)
    }
  }

  const onHandleSize = e => {
    const selectedSize = e.target.value
    setSize(selectedSize)
    handleSize(editor, selection, selectedSize)
  }

  const isButtonWrapper = selection
    .getChild(0)
    ?.getChild(0)
    ?.getChild(0)
    ?.hasClass('button-wrapper')

  const onHandleImageDelete = () => {
    setImageURL('')
    setBackground('')
    setImagePosition('center')
    setSize('SCALE_TO_FILL')
    handleImageDelete(editor, selection)
  }

  const handleImage = type => {
    track('media_gallery_in_editor_clicked')

    const onOk = item => {
      const itemUrl = item[0].url
      setImageURL(itemUrl)

      if (selection) {
        editor.model.change(writer => {
          const modelElement = editor.editing.mapper.toModelElement(selection)
          if (modelElement) {
            writer.setAttribute('backgroundImage', itemUrl, modelElement)
            writer.setAttribute('backgroundColor', '', modelElement)
            writer.setAttribute('backgroundSize', size, modelElement)
            writer.setAttribute(
              'backgroundPosition',
              imagePosition,
              modelElement,
            )

            const viewElement = editor.editing.mapper.toViewElement(
              modelElement,
            )
            if (viewElement) {
              editor.editing.view.change(writer => {
                writer.setStyle(
                  'background-image',
                  `url(${itemUrl})`,
                  viewElement,
                )
                writer.setStyle(
                  'background-position',
                  `${imagePosition}`,
                  viewElement,
                )
                const SizeType = {
                  SCALE_TO_FILL: { name: 'Scale to fill', class: 'cover' },
                  ORIGINAL_SIZE: { name: 'Original size', class: 'auto' },
                  TILE: { name: 'Tile', class: 'contain' },
                }
                writer.setStyle(
                  'background-size',
                  `${SizeType[size].class}`,
                  viewElement,
                )
              })
            }
          }
        })
      }

      dispatch(messageBoxHideAction('message-box-1'))
    }

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: `
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <Icon icon="ic:round-add-a-photo" color="#00AB55" width="24" height="24" />
              <Typography style="margin-left: 8; color: #002E47; fontWeight: 600; fontSize: 16;">
                Media Gallery
              </Typography>
            </div>`,
          component: (
            <ContainerMediaGalery onOk={onOk} setImageURL={setImageURL} />
          ),
          draggable: true,
          maxHeight: '70vh',
        }),
      ),
    )
  }

  const onHandleOverlayColor = event => {
    setOverlayColor(event)
    handleOverlayColor(editor, selection, event, opacityValue, imageURL)
  }

  const onChangePosition = event => {
    setImagePosition(event)
    handlePosition(editor, selection, event)
  }

  const onHandleColor = event => {
    setTextColor(event)
    handleColor(editor, selection, event)
  }
  const onHandleButtonWidth = (event, newSizing) => {
    if (newSizing !== null) {
      setButtonWidth(newSizing)
      editor.editing.view.focus()
      handleButtonWidth(editor, selection, newSizing)
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.box}>
        {isButtonWrapper && (
          <>
            <div className={classes.row}>
              <div>
                <Typography className={classes.header}>Alignment</Typography>
              </div>
              <CoreToggleButtonGroup
                value={alignment}
                onChange={onHandleAlignment}
                exclusive
                fullWidth
              >
                <CoreToggleButton value="left">
                  <Icon
                    className={classes.icon}
                    icon="proicons:text-align-left"
                  />
                </CoreToggleButton>
                <CoreToggleButton value="center">
                  <Icon
                    className={classes.icon}
                    icon="proicons:text-align-center"
                  />
                </CoreToggleButton>
                <CoreToggleButton value="right">
                  <Icon
                    className={classes.icon}
                    icon="proicons:text-align-right"
                  />
                </CoreToggleButton>
              </CoreToggleButtonGroup>
            </div>
            <div className={classes.row}>
              <div>
                <Typography className={classes.header}>Button Width</Typography>
              </div>
              <CoreToggleButtonGroup
                value={buttonWidth}
                onChange={onHandleButtonWidth}
                exclusive
                fullWidth
              >
                <CoreToggleButton value="fit">
                  <Typography className={classes.header}>Auto</Typography>
                </CoreToggleButton>
                <CoreToggleButton value="full">
                  <Typography className={classes.header}>Full</Typography>
                </CoreToggleButton>
              </CoreToggleButtonGroup>
            </div>
          </>
        )}
        <div className={classes.row}>
          <div>
            <Typography className={classes.header}>Background</Typography>
          </div>
          <CoreToggleButtonGroup
            value={background}
            onChange={onHandleBackground}
            exclusive
            fullWidth
          >
            <CoreToggleButton value="color">
              <Typography className={classes.header}>Color</Typography>
            </CoreToggleButton>
            <CoreToggleButton value="image">
              <Typography className={classes.header}>Image</Typography>
            </CoreToggleButton>
          </CoreToggleButtonGroup>
        </div>
        {background === 'color' ? (
          <div className={classes.row}>
            <div>
              <Typography className={classes.header}>Color</Typography>
            </div>
            <div className={classes.boxIconButton}>
              <div className={classes.inputItem}>
                <div className={classes.pickerBg}>
                  <span
                    className={classes.pickerSpan}
                    style={{
                      backgroundColor: textColor,
                      zIndex: '999999 !important',
                    }}
                    onClick={event => {
                      setPickerVisibles({
                        ...pickerVisibles,
                        fontColor: !pickerVisibles.fontColor,
                      })
                      setAnchorEl(event.currentTarget)
                    }}
                  ></span>
                  <Popover
                    className={classes.popover}
                    id={pickerVisibles.fontColor ? 'fontColor' : undefined}
                    open={pickerVisibles.fontColor}
                    anchorEl={anchorEl}
                    onClose={() => {
                      setPickerVisibles({ ...pickerVisibles, fontColor: false })
                      setAnchorEl(null)
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <ChromePicker
                      disabled={!pageReady}
                      color={textColor}
                      onChange={event => onHandleColor(event.hex)}
                    />
                  </Popover>
                </div>
              </div>
              <Typography className={classes.header}>{textColor}</Typography>
            </div>
          </div>
        ) : (
          <div className={classes.box}>
            <div className={classes.row}>
              <div>
                <Typography className={classes.header}>Source</Typography>
              </div>
              {imageURL === '' ? (
                <div onClick={handleImage} className={classes.boxSourceButton}>
                  <div className={classes.boxIcon}>
                    <Icon icon="proicons:photo" />
                  </div>
                  <div className={classes.uploadText}>
                    <Typography className={classes.logoText}>
                      Upload Image
                    </Typography>
                  </div>
                </div>
              ) : (
                <div className={classes.boxSourceImage}>
                  <img
                    className={classes.sourceImage}
                    src={imageURL}
                    alt="img"
                  />
                  <Icon
                    onClick={onHandleImageDelete}
                    className={classes.sourceIcon}
                    icon="iconoir:trash"
                  />
                </div>
              )}
            </div>
            {imageURL !== '' && (
              <div className={classes.box}>
                <div className={classes.row}>
                  <div>
                    <Typography className={classes.header}>
                      Image fill
                    </Typography>
                  </div>
                  <FormControl size="small">
                    <Select
                      fullWidth
                      variant="outlined"
                      id="size-select"
                      className="select"
                      value={size}
                      onChange={onHandleSize}
                    >
                      {Object.keys(SizeType).map(key => (
                        <MenuItem value={key} key={key}>
                          {SizeType[key].name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className={classes.row}>
                  <div>
                    <Typography className={classes.header}>
                      Image position
                    </Typography>
                  </div>
                  <div className={classes.backgroudPosition}>
                    <CoreBackgroundPosition
                      position={imagePosition}
                      onPositionChange={onChangePosition}
                    />
                  </div>
                </div>
                <div className={classes.row}>
                  <Divider className={classes.divider} />
                </div>
                <div className={classes.row}>
                  <div className={classes.overlay}>
                    <Typography className={classes.headerOverlay}>
                      Use image overlay
                    </Typography>
                    <CustomizedSwitch
                      onClick={onHandleChange}
                      checked={isChecked}
                    />
                  </div>
                </div>
                <div className={classes.row}>
                  <div>
                    <Typography className={classes.header}>
                      Image overlay color
                    </Typography>
                  </div>
                  <div className={classes.boxIconButton}>
                    <div className={classes.inputItem}>
                      <div className={classes.pickerBg}>
                        <span
                          className={classes.pickerSpan}
                          style={{
                            backgroundColor: overlayColor,
                            zIndex: '999999 !important',
                          }}
                          onClick={event => {
                            setPickerVisibles({
                              ...pickerVisibles,
                              fontColor: !pickerVisibles.fontColor,
                            })
                            setAnchorEl(event.currentTarget)
                          }}
                        ></span>
                        <Popover
                          className={classes.popover}
                          id={
                            pickerVisibles.fontColor ? 'fontColor' : undefined
                          }
                          open={pickerVisibles.fontColor}
                          anchorEl={anchorEl}
                          onClose={() => {
                            setPickerVisibles({
                              ...pickerVisibles,
                              fontColor: false,
                            })
                            setAnchorEl(null)
                          }}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                        >
                          <ChromePicker
                            disabled={!pageReady}
                            color={overlayColor}
                            onChange={event => onHandleOverlayColor(event.hex)}
                          />
                        </Popover>
                      </div>
                    </div>
                    <Typography className={classes.header}>
                      {overlayColor}
                    </Typography>
                  </div>
                </div>
                <div className={classes.row}>
                  <div className={classes.overlay}>
                    <Typography className={classes.header}>
                      Overlay opacity
                    </Typography>
                  </div>
                  <CoreSlider
                    initialValue={opacityValue}
                    fullWidth
                    height={4}
                    color={'#015FFB'}
                    thumbSize={16}
                    disabled={false}
                    step={1}
                    onChange={handleSliderChange}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default withStyles(styles)(BlockOptionsModal)
